/* ============================================================
   ROOK — Theme JavaScript
   ============================================================ */

'use strict';

/* --- Helpers --------------------------------------------- */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

const statusRegion = () => $('#rookStatus');
const announce = message => {
  const region = statusRegion();
  if (!region) return;
  region.textContent = '';
  requestAnimationFrame(() => { region.textContent = message; });
};
const setScrollLock = locked => {
  document.body.dataset.scrollLocked = String(locked);
};
const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function setInteractive(container, interactive) {
  if (!container) return;
  container.setAttribute('aria-hidden', String(!interactive));
  if ('inert' in container) {
    container.inert = !interactive;
    return;
  }
  $$(focusableSelector, container).forEach(element => {
    if (!interactive) {
      if (element.hasAttribute('tabindex')) element.dataset.previousTabindex = element.getAttribute('tabindex');
      element.setAttribute('tabindex', '-1');
    } else if (element.dataset.previousTabindex !== undefined) {
      element.setAttribute('tabindex', element.dataset.previousTabindex);
      delete element.dataset.previousTabindex;
    } else {
      element.removeAttribute('tabindex');
    }
  });
}

function trapFocus(event, container) {
  if (event.key !== 'Tab' || !container) return;
  const focusable = $$(focusableSelector, container).filter(el => el.offsetParent !== null);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function escapeHtml(value = '') {
  const node = document.createElement('div');
  node.textContent = value;
  return node.innerHTML;
}

/* --- Header scroll effect -------------------------------- */
function initHeader() {
  const header = $('.site-header');
  if (!header) return;

  const update = () => {
    header.classList.toggle('is-scrolled', window.scrollY > 40);
  };
  window.addEventListener('scroll', update, { passive: true });
  update();
}

/* --- Mobile menu ----------------------------------------- */
function initMobileMenu() {
  const toggle = $('.menu-toggle');
  const nav    = $('.mobile-nav');
  if (!toggle || !nav) return;
  setInteractive(nav, false);

  const open = () => {
    toggle.classList.add('is-open');
    nav.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    setInteractive(nav, true);
    setScrollLock(true);
    $('a', nav)?.focus();
  };
  const close = (returnFocus = true) => {
    toggle.classList.remove('is-open');
    nav.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    setInteractive(nav, false);
    setScrollLock(false);
    if (returnFocus) toggle.focus();
  };

  toggle.addEventListener('click', () => toggle.classList.contains('is-open') ? close() : open());
  $$('a', nav).forEach(a => a.addEventListener('click', () => close(false)));
  document.addEventListener('keydown', event => {
    if (!nav.classList.contains('is-open')) return;
    if (event.key === 'Escape') close();
    trapFocus(event, nav);
  });
}

/* --- Hero animate-in ------------------------------------- */
function initHero() {
  const hero = $('.hero');
  if (!hero) return;
  requestAnimationFrame(() => hero.classList.add('is-loaded'));
}

/* --- Scroll reveal --------------------------------------- */
function revealAll(ctx = document) {
  $$(`.reveal`, ctx).forEach(el => el.classList.add('is-visible'));
}

function initReveal() {
  // In the Shopify theme editor, sections are already on screen and scrolling
  // is not simulated — skip the observer and show everything immediately.
  if (window.Shopify && window.Shopify.designMode) {
    revealAll();
    return;
  }

  if (!('IntersectionObserver' in window)) {
    revealAll();
    return;
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.classList.add('is-visible');
        io.unobserve(e.target);
      }
    });
  }, { threshold: 0.12 });

  $$('.reveal').forEach(el => io.observe(el));
}

// When the theme editor reloads a section after a settings change, re-reveal
// any new .reveal elements that were injected into that section.
document.addEventListener('shopify:section:load', (e) => {
  if (window.Shopify && window.Shopify.designMode) {
    revealAll(e.target);
  }
});

/* --- Cart drawer ----------------------------------------- */
let cartDrawer, cartOverlay, cartBody, cartCount, cartOpener;

function initCartDrawer() {
  cartDrawer  = $('.cart-drawer');
  cartOverlay = $('#siteOverlay');
  cartBody    = $('.cart-drawer__body');
  cartCount   = $$('[data-cart-count]');

  if (!cartDrawer) return;
  setInteractive(cartDrawer, false);

  $$('[data-cart-open]').forEach(btn => btn.addEventListener('click', event => openCart(event.currentTarget)));
  $$('[data-cart-close]').forEach(btn => btn.addEventListener('click', closeCart));
  cartOverlay?.addEventListener('click', closeCart);

  document.addEventListener('keydown', e => {
    if (!cartDrawer?.classList.contains('is-open')) return;
    if (e.key === 'Escape') closeCart();
    trapFocus(e, cartDrawer);
  });
}

function openCart(opener) {
  cartOpener = opener || document.activeElement;
  cartDrawer?.classList.add('is-open');
  setInteractive(cartDrawer, true);
  cartOverlay?.classList.add('is-active');
  if (cartOverlay) cartOverlay.hidden = false;
  $$('[data-cart-open]').forEach(btn => btn.setAttribute('aria-expanded', 'true'));
  setScrollLock(true);
  $('[data-cart-close]', cartDrawer)?.focus();
  refreshCartDrawer();
}

function closeCart() {
  if (!cartDrawer?.classList.contains('is-open')) return;
  cartDrawer?.classList.remove('is-open');
  setInteractive(cartDrawer, false);
  cartOverlay?.classList.remove('is-active');
  $$('[data-cart-open]').forEach(btn => btn.setAttribute('aria-expanded', 'false'));
  setScrollLock(false);
  window.setTimeout(() => { if (cartOverlay) cartOverlay.hidden = true; }, 350);
  cartOpener?.focus?.();
}

async function refreshCartDrawer() {
  if (!cartDrawer) return;
  try {
    const res  = await fetch('/cart.js');
    const data = await res.json();
    renderCartDrawer(data);
    updateCartCount(data.item_count);
  } catch (err) {
    console.error('Cart refresh failed', err);
    announce('We could not refresh your cart. Please try again.');
  }
}

function updateCartCount(count) {
  cartCount?.forEach(el => {
    el.textContent = count;
    el.dataset.count = count;
  });
}

function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  const fmt = (window.Rook && window.Rook.moneyFormat) || '{{amount}}';
  return fmt.replace(/\{\{\s*amount[^}]*\}\}/g, amount);
}

function renderCartDrawer(cart) {
  if (!cartBody) return;
  if (!cart.item_count) {
    cartBody.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
    const amt = $('.cart-subtotal__amount');
    if (amt) amt.textContent = formatMoney(0);
    return;
  }

  const items = cart.items.map(item => `
    <div class="cart-item" data-key="${item.key}">
      <div class="cart-item__image">
        ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy" width="80" height="80">` : ''}
      </div>
      <div class="cart-item__info">
        <div class="cart-item__title">${escapeHtml(item.product_title)}</div>
        <div class="cart-item__variant">${escapeHtml(item.variant_title || '')}</div>
        <div class="cart-item__price">${formatMoney(item.line_price)}</div>
        <button class="cart-item__remove" data-key="${escapeHtml(item.key)}" aria-label="Remove ${escapeHtml(item.product_title)} from cart">Remove</button>
      </div>
    </div>
  `).join('');

  cartBody.innerHTML = items;

  const amt = $('.cart-subtotal__amount');
  if (amt) amt.textContent = formatMoney(cart.total_price);

  $$('.cart-item__remove', cartBody).forEach(btn => {
    btn.addEventListener('click', () => removeCartItem(btn.dataset.key));
  });
}

async function removeCartItem(key) {
  try {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
    if (!response.ok) throw new Error('Cart update failed');
    await refreshCartDrawer();
    announce('Item removed from cart.');
  } catch (err) {
    console.error('Remove item failed', err);
    announce('We could not remove that item. Please try again.');
  }
}

/* --- Add to cart ----------------------------------------- */
function initAddToCart() {
  const form = $('[data-atc-form]');
  if (!form) return;

  form.addEventListener('submit', async e => {
    e.preventDefault();
    const btn = $('[data-atc-btn]', form);
    const originalText = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<span class="spinner"></span>';

    try {
      const formData = new FormData(form);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id:       formData.get('id'),
          quantity: parseInt(formData.get('quantity') || 1, 10),
        }),
      });
      if (!response.ok) throw new Error('Add to cart failed');
      await refreshCartDrawer();
      announce('Added to cart.');
      openCart(btn);
    } catch (err) {
      console.error('Add to cart failed', err);
      announce('We could not add that watch to your cart. Please try again.');
    } finally {
      btn.disabled = false;
      btn.innerHTML = originalText;
    }
  });

  $('[data-sticky-atc]')?.addEventListener('click', () => form.requestSubmit());

  const purchaseBar = $('[data-mobile-purchase-bar]');
  const primaryButton = $('[data-atc-btn]', form);
  if (purchaseBar && primaryButton) {
    let stickyUpdateScheduled = false;
    const updateStickyBar = () => {
      purchaseBar.classList.toggle('is-visible', primaryButton.getBoundingClientRect().bottom < 0);
      stickyUpdateScheduled = false;
    };
    const scheduleStickyUpdate = () => {
      if (stickyUpdateScheduled) return;
      stickyUpdateScheduled = true;
      requestAnimationFrame(updateStickyBar);
    };
    window.addEventListener('scroll', scheduleStickyUpdate, { passive: true });
    window.addEventListener('resize', scheduleStickyUpdate, { passive: true });
    updateStickyBar();
  }
}

/* --- Variant selector ------------------------------------ */
function initVariantSelector() {
  const selector = $('[data-variant-selector]');
  if (!selector) return;

  const groups = $$('.variant-selector', selector);
  const variantInput = $('[name="id"]');
  const priceEl = $('.product-info__price');
  const stickyPrice = $('[data-sticky-price]');
  const stickyAvailability = $('[data-sticky-availability]');
  const stickyButton = $('[data-sticky-atc]');
  const atcButton = $('[data-atc-btn]');

  // Build variant map from data attr
  let variantData = [];
  try {
    variantData = JSON.parse(selector.dataset.variants || '[]');
  } catch {}

  const selectedValues = () => groups.map(group =>
    $('.variant-option.is-active', group)?.dataset.optionValue || ''
  );

  function updateVariant(writeHistory = true) {
    const values = selectedValues();
    const variant = variantData.find(candidate =>
      candidate.options?.every((value, index) =>
        String(value).toLowerCase() === String(values[index]).toLowerCase()
      )
    );

    if (variant) {
      if (variantInput) variantInput.value = variant.id;
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      if (stickyAvailability) stickyAvailability.textContent = variant.available ? 'In stock' : 'Sold out';
      [atcButton, stickyButton].forEach(button => {
        if (!button) return;
        button.disabled = !variant.available;
        button.textContent = variant.available ? 'Add to cart' : 'Sold out';
      });
      if (writeHistory) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    // Swap gallery image if variant has featured_image
    if (variant?.featured_image?.id) {
      const slide = $(`[data-image-id="${variant.featured_image.id}"]`);
      const track = $('[data-gallery-track]');
      if (slide && track) track.scrollTo({ left: slide.offsetLeft, behavior: writeHistory ? 'smooth' : 'auto' });
    }
  }

  groups.forEach(group => {
    const options = $$('.variant-option', group);
    if (!$('.variant-option.is-active', group) && options[0]) {
      options[0].classList.add('is-active');
      options[0].setAttribute('aria-pressed', 'true');
    }
    options.forEach(btn => {
      btn.addEventListener('click', () => {
        options.forEach(option => {
          option.classList.toggle('is-active', option === btn);
          option.setAttribute('aria-pressed', String(option === btn));
        });
        updateVariant();
      });
    });
  });

  updateVariant(false);
}

/* --- Gallery thumbnails ---------------------------------- */
function initGallery() {
  const thumbs  = $$('.product-gallery__thumb');
  const track = $('[data-gallery-track]');
  const slides = $$('[data-gallery-slide]');
  const indicator = $('[data-gallery-indicator]');
  if (!track || !slides.length) return;

  const setActive = index => {
    const safeIndex = Math.max(0, Math.min(index, slides.length - 1));
    thumbs.forEach((thumb, thumbIndex) => {
      const active = thumbIndex === safeIndex;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', String(active));
    });
    if (indicator) indicator.textContent = `${safeIndex + 1} / ${slides.length}`;
  };

  thumbs.forEach((thumb, index) => {
    thumb.addEventListener('click', () => {
      track.scrollTo({ left: slides[index]?.offsetLeft || 0, behavior: 'smooth' });
      setActive(index);
    });
  });

  let scheduled = false;
  track.addEventListener('scroll', () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
      setActive(index);
      scheduled = false;
    });
  }, { passive: true });
  track.addEventListener('keydown', event => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const current = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
    const next = current + (event.key === 'ArrowRight' ? 1 : -1);
    const slide = slides[Math.max(0, Math.min(next, slides.length - 1))];
    track.scrollTo({ left: slide?.offsetLeft || 0, behavior: 'smooth' });
  });
}

/* --- Quantity input -------------------------------------- */
function initQuantity() {
  const wrap = $('.quantity-input');
  if (!wrap) return;

  const input = $('input[name="quantity"]', wrap);
  const dec   = $('[data-qty-dec]', wrap);
  const inc   = $('[data-qty-inc]', wrap);

  dec?.addEventListener('click', () => {
    const val = parseInt(input.value, 10);
    if (val > 1) input.value = val - 1;
  });
  inc?.addEventListener('click', () => {
    input.value = parseInt(input.value, 10) + 1;
  });
}

/* --- Product detail accordions --------------------------- */
function initAccordions() {
  $$('.product-detail-toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      const body = btn.nextElementSibling;
      const isOpen = btn.classList.contains('is-open');
      btn.classList.toggle('is-open', !isOpen);
      btn.setAttribute('aria-expanded', String(!isOpen));
      body?.classList.toggle('is-open', !isOpen);
    });
  });
}

/* --- Boot ----------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initMobileMenu();
  initHero();
  initReveal();
  initCartDrawer();
  initAddToCart();
  initVariantSelector();
  initGallery();
  initQuantity();
  initAccordions();
});
